import styled from "styled-components";
import { Label as BaseLabel } from "../ui";

const Panel = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 1.25rem;
  z-index: 1000;
  font-family: "Silkscreen", monospace;
  background: ${({ theme }) => theme.colors("background", 0.92)};
  border: 1px solid ${({ theme }) => theme.colors("foreground", 0.2)};
  padding: 0.75rem 1rem;
  min-width: 200px;
  max-width: 260px;
  box-shadow: 0 0 20px ${({ theme }) => theme.colors("shadow", 0.7)};
  @media (max-width: 480px) {
    bottom: calc(4rem + env(safe-area-inset-bottom, 0px));
    left: 0;
    right: 0;
    max-width: 100%;
    min-width: unset;
    border-left: none;
    border-right: none;
    padding: 0.5rem 1rem;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.75rem;
  }
`;

const Label = styled(BaseLabel)`
  display: block;
  font-size: 0.5rem;
  letter-spacing: 0.2em;
  color: ${({ theme }) => theme.colors("foreground", 0.3)};
  text-transform: uppercase;
  margin-right: 0;
  margin-bottom: 0.4rem;
  @media (max-width: 480px) {
    display: none;
  }
`;

const Info = styled.div`
  @media (max-width: 480px) {
    flex: 1;
    min-width: 0;
  }
`;

const Name = styled.div`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors("text")};
  margin-bottom: 0.2rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Meta = styled.div`
  font-size: 0.58rem;
  color: ${({ theme }) => theme.colors("foreground", 0.45)};
  margin-bottom: 0.6rem;
  @media (max-width: 480px) {
    margin-bottom: 0;
  }
`;

// `$boldHover` reproduces the pinmonkey lobby's original filled hover state
// (link border + text + chrome fill), vs. the home page's original subtle
// hover (foreground dim). Both were identical otherwise, so this is the one
// real divergence between the two call sites — kept explicit rather than
// silently picking one and changing the other page's look.
const Btn = styled.button`
  background: none;
  border: 1px solid
    ${({ $primary, theme }) =>
      $primary ? theme.colors("link") : theme.colors("foreground", 0.15)};
  color: ${({ $primary, theme }) =>
    $primary ? theme.colors("text") : theme.colors("foreground", 0.45)};
  font-family: "Silkscreen", monospace;
  font-size: 0.62rem;
  letter-spacing: 0.1em;
  padding: 0.35rem 0.7rem;
  cursor: pointer;
  text-transform: uppercase;
  &:hover {
    ${({ $boldHover, theme }) =>
      $boldHover
        ? `
          border-color: ${theme.colors("link")};
          color: ${theme.colors("text")};
          background: ${theme.colors("chrome")};
        `
        : `
          border-color: ${theme.colors("foreground", 0.35)};
          color: ${theme.colors("foreground", 0.7)};
        `}
  }
`;

const classMeta = (save) => {
  const cls = save?.character?.class;
  if (!cls) return "";
  return cls.charAt(0).toUpperCase() + cls.slice(1);
};

// Bottom-left HUD panel showing the active character, or a login/select prompt.
// Shared by the home map and the pinmonkey dungeon lobby.
export const CharacterPanel = ({
  isLoggedIn,
  activeCharacter,
  onLogin,
  onChangeCharacter,
  onSelectCharacter,
  $boldHover,
}) => (
  <Panel>
    <Label>Character</Label>
    {!isLoggedIn ? (
      <>
        <Info>
          <Meta>Login to play</Meta>
        </Info>
        <Btn $primary $boldHover={$boldHover} onClick={onLogin}>
          Login
        </Btn>
      </>
    ) : activeCharacter ? (
      <>
        <Info>
          <Name>
            {activeCharacter.character?.name ||
              activeCharacter.name ||
              "Unnamed"}
          </Name>
          <Meta>
            {classMeta(activeCharacter)}
            {activeCharacter.stats?.level
              ? ` · Lv ${activeCharacter.stats.level}`
              : ""}
            {activeCharacter.stats?.health != null
              ? ` · ${activeCharacter.stats.health} HP`
              : ""}
          </Meta>
        </Info>
        <Btn $boldHover={$boldHover} onClick={onChangeCharacter}>Change</Btn>
      </>
    ) : (
      <>
        <Info>
          <Meta>No character selected</Meta>
        </Info>
        <Btn $primary $boldHover={$boldHover} onClick={onSelectCharacter}>
          Select Character
        </Btn>
      </>
    )}
  </Panel>
);
